import type {
  AdventureVersion,
  MeritBadgeVersion,
  RankVersion,
} from '@appTypes/common/versions';
import { PendingItemStatus } from '@appTypes/enums';
import type {
  AdvancementHistoryItem,
  Adventure,
  AdventureRequirement,
  AdventureUserRequirements,
  Award,
  AwardRequirement,
  AwardUserRequirements,
  AwardVersion,
  RankRequirement as ESBRankRequirement,
  GetUserAdventureRequirementsRes,
  MeritBadge,
  MeritBadgeRequirement,
  MeritBadgeUserRequirements,
  RankV1,
  RecordedInformation,
  UserRankVersionRequirementInfo,
} from '@appTypes/esb';
import type { RosterScout } from '@modules/advancement/duck/types';

import { QEValidationResult } from './utils/validators';

export type RankRequirement = ESBRankRequirement;

export type BulkEntrySupportedVersion =
  | AdventureVersion
  | MeritBadgeVersion
  | RankVersion
  | AwardVersion;

export type AdvancementRequirement =
  | MeritBadgeRequirement
  | RankRequirement
  | AdventureRequirement
  | AwardRequirement;

export type Rank = RankV1;

export enum BulkEntryType {
  MERIT_BADGES = 'merit-badges',
  RANKS = 'ranks',
  AWARDS = 'awards',
  ADVENTURES = 'adventures',
}

export type AdvancementHistoryItemBySubUnit = {
  subUnitName: string;
  subUnitId: number;
  records: AdvancementHistoryItem[];
};

export type BlueCardsDictionary = {
  [subUnitId: string]: {
    [userId: string]: {
      [meritBadgeId: string]: boolean;
    };
  };
};

interface RecordedInformationWithImage extends RecordedInformation {
  imageUrl100: string;
}
export interface AdvancementHistoryItemWithImage
  extends AdvancementHistoryItem {
  recordedInformation: RecordedInformationWithImage[];
}

export type BlueCardsFilters = {
  selectedYouths: number[];
  meritBadgeIds: number[];
  advancementStatus: PendingItemStatus[];
  fromDate: string | null;
  toDate: string | null;
};

/**
 * Redux slice for quickEntryRank
 */
export interface BulkEntryState {
  // START common
  entryType?: BulkEntryType;
  selectedVersion?: BulkEntrySupportedVersion;
  requirements: AdvancementRequirement[];
  preselectedInfo: {
    rank?: Rank;
    meritBadge?: MeritBadge;
    adventure?: Adventure;
    award?: Award;
    version?: BulkEntrySupportedVersion;
  };
  comment: string;
  /** User IDs of selected Scouts */
  selectedScouts: number[];
  /** Requirement IDs of selected Requirements */
  selectedRequirements: number[];
  markAsApproved?: boolean;
  completionDate?: Date;
  /**
   * List of validation errors for the Scouts indexed by user id.
   *
   *  `{ [userId]: QEValidationResult[] }`
   **/
  validationResults: Record<number, QEValidationResult[]>;
  // END common

  // Adventures
  selectedAdventure?: Adventure;
  adventureUserRequirements: AdventureUserRequirements[];

  // Awards
  selectedAward?: Award;
  awardUserRequirements: AwardUserRequirements[];

  // Merit Badge specific
  selectedMeritBadge?: MeritBadge;
  /** If user is selecting one or multiple Merit Badges to apply */
  mbEntryType: 'single' | 'multiple';
  selectedMeritBadges: MeritBadge['id'][];
  meritBadgeUserRequirements: MeritBadgeUserRequirements[];

  // Ranks specific
  selectedRank?: Rank;
  scoutsRankVersionInfo: UserRankVersionRequirementInfo[];

  // Blue Card
  blueCardFilters: BlueCardsFilters;
  filteredMeritBadges: AdvancementHistoryItemBySubUnit[];
  selectedBlueCards: BlueCardsDictionary;
}

// Used in the store state to save data for all bulk entries

export interface CommonValidatorParams {
  scout: RosterScout;
  selectedVersion?: BulkEntrySupportedVersion;
  selectedRank?: Rank;
  // adventures
  selectedAdventure?: Adventure;
  userAdventureRequirements?: GetUserAdventureRequirementsRes;
}

export interface CommonErrorParams extends CommonValidatorParams {
  message: string;
}
